import { GetServerSideProps } from 'next';
import { getMerchGroupBySlug, getMerchGroups } from '@components/shared/shop/model/MerchGroup';
import {
  getSelectedShopGroupChain,
  getTopLevelShopGroups,
  setSelectedShopGroup
} from '@components/shared/shop/model/ShopGroup';
import { addApolloState } from '@lib/apollo/apolloClient';
import formatShopSuggestions
  from '@components/pages/ShopV3/components/ShopSuggestions/formatShopSuggestions';
import {
  fetchShopNavigationMenu,
  IShopNavigationMenu
} from '@components/shared/shop/ShopNavigationMenu';
import { getQueryParam } from '@lib/url';
import { IMerchGroup } from '@models/MerchGroup/i-merch-group';
import {
  IShopPageData,
  ShopPageType
} from '@components/pages/ShopV3/model/ShopPage';
import fetchPrismicDocument from '@lib/prismic/fetchPrismicDocument';
import { asText, optionalImage } from '@lib/prismic/util/document';
import logger from '@lib/logger';
import {
  createSearchCriteria,
  getApolloForSearch,
  parseFacets,
  productSearch
} from '@components/pages/ShopV3/util/search';
import { allFulfilled } from '@lib/promise';
import ShopPage from '@components/pages/ShopV3/ShopPage';
import env from '@lib/envs/env';

const log = logger('ShopMerchGroup');

export default ShopPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    query,
    params,
    previewData,
  } = context;

  const slug = getQueryParam(params!, 'slug')!;

  const apolloClient = getApolloForSearch();

  const [
    merchGroups,
    shopGroupsResult,
    selectedMerchGroup,
  ] = await allFulfilled([
    { name: 'getMerchGroups', promise: getMerchGroups(apolloClient) },
    { name: 'getTopLevelShopGroups', promise: getTopLevelShopGroups(apolloClient) },
    { name: 'getMerchGroupBySlug', promise: getMerchGroupBySlug(apolloClient, slug) },
  ], log);

  if (!selectedMerchGroup) {
    return { notFound: true };
  }

  const shopGroups = setSelectedShopGroup(shopGroupsResult, selectedMerchGroup);

  const shopPageData = await getMerchGroupShopPageData(selectedMerchGroup, previewData);

  const searchCriteria = createSearchCriteria({
    query,
    selectedMerchGroup,
  });

  try {
    const [productSearchResult, shopNavigationMenuResult] = await Promise.allSettled([
      productSearch(apolloClient, searchCriteria),
      fetchShopNavigationMenu({
        previewData,
        apolloClient,
        merchGroups,
        merchGroupCategories: shopGroups,
      }),
    ]);

    let searchResponse: Awaited<ReturnType<typeof productSearch>>;
    if (productSearchResult.status === 'fulfilled') {
      searchResponse = productSearchResult.value;
    } else {
      log.error('API call failed: productSearch');
      log.error(productSearchResult.reason);
      return { notFound: true };
    }

    let shopNavigationMenu: Awaited<ReturnType<typeof fetchShopNavigationMenu>>;
    if (shopNavigationMenuResult.status === 'fulfilled') {
      shopNavigationMenu = shopNavigationMenuResult.value;
    } else {
      log.error('API call failed: fetchShopNavigationMenu', shopNavigationMenuResult.reason);
      log.error(shopNavigationMenuResult.reason);
      return { notFound: true };
    }

    if (!searchResponse.data?.searchShopProducts) {
      log.error('searchResponse is undefined or missing data');
      return { notFound: true };
    }

    return addApolloState(apolloClient, {
      props: {
        query: searchCriteria.query || null,
        slugs: [slug] || null,
        shopPageType: ShopPageType.MERCH_GROUP,
        searchCriteria,
        searchFilters: {
          shopGroups,
          selectedMerchGroup,
          selectedShopGroupChain: getSelectedShopGroupChain(shopGroups) || [],
          selectedVendorGroupChain: [],
        },
        searchResults: {
          ...searchResponse.data.searchShopProducts,
          facets: parseFacets(searchResponse.data.searchShopProducts.facets),
        },
        shopPageData,
        shopNavigationMenu,
        footerImages: formatShopSuggestions(
          shopNavigationMenu.map((m: IShopNavigationMenu) => m.option)
        ),
      },
    });
  } catch (error) {
    log.error('Unexpected error during API calls:', error);
    return { notFound: true };
  }
};

async function getMerchGroupShopPageData(
  selectedMerchGroup: IMerchGroup,
  previewData: any
) {
  const shopPageData: IShopPageData = {
    title: selectedMerchGroup!.title,
    description: selectedMerchGroup.description,
    image: selectedMerchGroup.heroImageUrl,
    featuredItems: [],
  };

  if (!env.HAVENLY_FEATURE_DB_DRIVEN_MERCH_GROUP) {
    try {
      const prismicDoc = await fetchPrismicDocument(
        'merch_group_page',
        selectedMerchGroup.id,
        { previewData }
      );

      if (prismicDoc?.data) {
        const { data } = prismicDoc;
        shopPageData.description = asText(data.description, shopPageData.description);
        shopPageData.image = optionalImage(data.hero_image);
      } else {
        log.debug({
          merchGroup: selectedMerchGroup.id
        }, 'No Prismic data for merch group');
      }
    } catch (ex) {
      log.warn({
        merchGroup: selectedMerchGroup.id
      },
        `Unable to retrieve Prismic data for merch group ${selectedMerchGroup?.id}`,
        ex);
    }
  }

  return shopPageData;
}
